import Header from '@components/containers/header';
import FAQMetaTagHead from '@components/head/faqMetadata';
import FaviconHead from '@components/head/favicons';
import HreflangHead from '@components/head/hreflangHead';
import AppLayout from '@components/layouts/appLayout';
import TextAnswersArray from '@components/textAnswers/textAnswersArray';
import { ROUTE_FAQ, SECONDS_PER_DAY } from '@constants';
import {
    Enum_Modules_Type,
    GetTextAnswersAndModulesByProductDocument,
    GetTextAnswersAndModulesByProductQuery,
    TextAnswerFragment,
} from '@graphql/generated/graphql';
import { PageProps } from '@types';
import { buildHreflangArray } from '@utils/hreflang';
import {
    withApollo,
    withCompany,
    withErrorsGSP,
    withProduct,
} from '@utils/serverMethodPlugins';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';

interface Props extends PageProps {
    title: string;
    textAnswers: TextAnswerFragment[];
}

const FAQPage: NextPage<Props> = ({
    title,
    textAnswers,
    hrefLang,
    serverCompanyContext,
    serverProductContext,
}) => (
    <>
        <FAQMetaTagHead pageTitle={title} textAnswers={textAnswers} />
        <FaviconHead productMeta={serverProductContext.meta} />
        <HreflangHead
            companySlug={serverCompanyContext.meta.Slug!}
            defaultLocale={serverCompanyContext.meta.DefaultLocale!}
            refs={hrefLang}
        />
        <AppLayout
            header={
                <Header
                    left={{ display: 'back' }}
                    center={{ display: 'text', text: { title } }}
                />
            }
            body={<TextAnswersArray textAnswers={textAnswers} />}
        />
    </>
);

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps: GetStaticProps = withErrorsGSP<Props>(
    withApollo<Props>(
        withCompany<Props>(
            withProduct<Props>(async ({ apolloClient, locale, params }) => {
                const {
                    data: { products },
                } =
                    await apolloClient.query<GetTextAnswersAndModulesByProductQuery>(
                        {
                            query: GetTextAnswersAndModulesByProductDocument,
                            variables: {
                                locale,
                                companySlug: params.company,
                                productSlug: params.product,
                            },
                        }
                    );

                const product = products?.[0];

                if (!product) {
                    return { notFound: true };
                }

                if (products.length !== 1) {
                    throw Error(
                        `Received invalid number of products from GetTextAnswers(companySlug: ${params.company}) in GSP at path ${ROUTE_FAQ}.tsx`
                    );
                }

                const moduleFAQ = product.Modules?.find(
                    (module) => module?.Module?.Type === Enum_Modules_Type.Faq
                );

                if (!product.Text_answers?.length || !moduleFAQ?.Module) {
                    return { notFound: true };
                }

                return {
                    props: {
                        title: moduleFAQ.Module.Title,
                        textAnswers: product.Text_answers,
                        hrefLang: buildHreflangArray({
                            entity: moduleFAQ,
                            currentLocale: locale!,
                            basePath: `/${params.product}/faq`,
                        }),
                    },
                    revalidate: SECONDS_PER_DAY,
                };
            })
        )
    )
);

export default FAQPage;
